import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';

import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '../../../../shared/helpers/ordenacao-tabela.helper';
import { RouterModule } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmModalComponent } from '../../../../shared/components/confirm-modal/confirm-modal.component';
import { EmptyListComponent } from '../../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../../shared/components/loading-box/loading-box.component';
import { ModuloEnum } from '../../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../../shared/services/alert.service';
import { VariacaoService } from '../../../../shared/services/apis/produto/variacao.service';
import { VariacaoStruct } from '../../../../shared/services/structs/produto/variacao.struct';
import { UtilService } from '../../../../shared/services/util.service';
import { HeaderComponent } from '../../../../shared/components/header/header.component';
import { LoadingBoxDefaultListComponent } from '../../../../shared/components/loading-box-default-list/loading-box-default-list.component';

@Component({
  selector: 'app-config-variacoes',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatCheckboxModule,
    MatMenuModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatDialogModule,
    EmptyListComponent,
    RouterModule,
    HeaderComponent,
    LoadingBoxDefaultListComponent,
  ],
  templateUrl: './config-variacoes.component.html',
  styleUrl: './config-variacoes.component.css',
})
export class ConfigVariacoesComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;

  public model: FormGroup;
  public _paginaAtual: number = 1;
  public _tamanhoPagina: number = 20;
  public _totalRegistros: number = 0;

  public _dataSource = new DataSourceOrdenacaoInsensivel<VariacaoStruct>();
  public _isLoading: boolean = false;

  public displayedColumns: string[] = ['select', 'nome', 'valores', 'actions'];

  public selection = new SelectionModel<VariacaoStruct>(true, []);

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
  }

  constructor(
    private formBuilder: FormBuilder,
    private variacaoService: VariacaoService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private utilService: UtilService
  ) {}

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    this.loadFilters();
    this.carregarVariacoes();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/configuracoes/cadastros/variacoes/cadastrar',
    ]);
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  public carregarVariacoes(reset: boolean = false): void {
    this._isLoading = true;
    const nome = this.model.get('pesquisa')?.value || '';

    this.variacaoService.getAll(nome, this._paginaAtual, this._tamanhoPagina).subscribe({
      next: response => {
        this._isLoading = false;
        if (response.isError) {
          this.alertService.show(
            'Erro',
            response.errorDescription || 'Erro ao carregar variações',
            AlertType.error
          );
          return;
        }

        this._totalRegistros = response.totalRegistros;

        if (reset) {
          this._dataSource.data = response.listaVariacoesProduto;
          this.selection.clear();
        } else {
          this._dataSource.data = [...this._dataSource.data, ...response.listaVariacoesProduto];
        }
      },
      error: err => {
        this._isLoading = false;
        this.alertService.show(
          'Erro inesperado',
          err.message || 'Erro ao carregar variações',
          AlertType.error
        );
      },
    });
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.carregarVariacoes();
  }

  public clickPesquisar(): void {
    this._paginaAtual = 1;
    this._dataSource.data = [];
    this.selection.clear();
    this.carregarVariacoes(true);
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this._dataSource.data.length;
    return numSelected === numRows && numRows > 0;
  }

  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }
    this.selection.select(...this._dataSource.data);
  }

  public hasLinkedProducts(variacao: VariacaoStruct): boolean {
    return variacao.listaValores?.some(v => v.possuiProdutos) ?? false;
  }

  public abrirModalExcluir(item?: VariacaoStruct): void {
    const isBulk = !item;
    const itemsToDelete = isBulk ? this.selection.selected : [item!];

    if (itemsToDelete.length === 0) return;

    const possuiVinculo = itemsToDelete.some(i => this.hasLinkedProducts(i));

    let title = 'Confirmar exclusão';
    let description = '';

    if (isBulk) {
      if (possuiVinculo) {
        description =
          'Algumas variações selecionadas estão vinculadas a produtos e NÃO serão excluídas. Deseja continuar e excluir apenas as não vinculadas?';
      } else {
        description = 'Tem certeza que deseja excluir as variações selecionadas?';
      }
    } else {
      if (this.hasLinkedProducts(item!)) {
        this.alertService.show(
          'Atenção',
          'Esta variação está vinculada a produtos e não pode ser excluída.',
          AlertType.warning
        );
        return;
      }
      description = 'Tem certeza que deseja excluir esta variação?';
    }

    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: title,
        description: description,
        buttonConfirm: 'Confirmar',
        buttonClose: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.confirm) {
        this._isLoading = true;

        if (isBulk) {
          const ids = itemsToDelete.filter(i => !this.hasLinkedProducts(i)).map(i => i.idVariacao);

          if (ids.length === 0) {
            this._isLoading = false;
            this.alertService.show(
              'Atenção',
              'Nenhuma das variações selecionadas pode ser excluída pois todas possuem vínculo.',
              AlertType.warning
            );
            return;
          }

          this.variacaoService.deleteBulk(ids).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  `Erro ao excluir itens: ${response.errorDescription}`,
                  AlertType.error
                );
              } else {
                const msg = possuiVinculo
                  ? 'Apenas as variações sem vínculo foram excluídas.'
                  : 'Variações excluídas com sucesso';
                const type = possuiVinculo ? AlertType.warning : AlertType.success;

                this.alertService.show('Sucesso', msg, type);
                this.selection.clear();
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir variações',
                AlertType.error
              );
            },
          });
        } else {
          this.variacaoService.delete(item!.idVariacao).subscribe({
            next: response => {
              this._isLoading = false;
              if (response.isError) {
                this.alertService.show(
                  'Erro',
                  response.errorDescription || 'Erro ao excluir variação',
                  AlertType.error
                );
              } else {
                this.alertService.show('Sucesso', 'Variação excluída com sucesso', AlertType.success);
                this.clickPesquisar();
              }
            },
            error: err => {
              this._isLoading = false;
              this.alertService.show(
                'Erro inesperado',
                err.message || 'Erro ao excluir variação',
                AlertType.error
              );
            },
          });
        }
      }
    });
  }
}
